// Directional buffer resolution, mirroring ember's src/utils/bufferUtils.ts so the calendar renders
// buffers the same way the backend validates them. A booking may carry a buffer on only one side
// (hasPreBuffer / hasPostBuffer); the legacy single `hasBuffers` flag is the fallback when the
// directional fields are absent, and both default ON when nothing is set.

export interface BufferFlags {
  /** Buffer before the booking (drawn above it on the calendar). */
  pre: boolean;
  /** Buffer after the booking (drawn below it on the calendar). */
  post: boolean;
}

interface BufferFlagSource {
  hasPreBuffer?: boolean;
  hasPostBuffer?: boolean;
  hasBuffers?: boolean;
}

/**
 * Resolve a booking's effective directional buffer flags. Directional fields win; else fall back to
 * the legacy single `hasBuffers`; else default both ON. Do NOT gate rendering on the combined
 * `hasBuffers` — it is persisted as (pre && post), so a single-sided buffer collapses it to false.
 */
export function resolveBufferFlags(b: BufferFlagSource): BufferFlags {
  const legacy = b.hasBuffers;
  return {
    pre: b.hasPreBuffer ?? legacy ?? true,
    post: b.hasPostBuffer ?? legacy ?? true,
  };
}

/** True when the booking has a buffer on at least one side — the correct gate for whether to render buffers at all. */
export function hasAnyBuffer(b: BufferFlagSource): boolean {
  const { pre, post } = resolveBufferFlags(b);
  return pre || post;
}
